// The daily costing engine (PRD §3.1) and the load strip (§3.9). Promoted from the P0 grid
// prototype, where it ran a 15 × 160-week plan in about 0.4 ms. Pure: it runs in the browser on
// every edit and in the API as the authoritative recompute on save.

import { addDays, monthOf, type Iso, type Month } from "./dates";

/** A cost rate in $/h, effective from `from` until the next step. Sorted by `from`. */
export interface RateStep {
  from: Iso;
  rate: number;
}

export interface CostRow {
  /** null for a placeholder, which costs at its role's standard rate and has no load strip. */
  person: string | null;
  rates: readonly RateStep[];
}

export interface Calendar {
  weekStarts: Iso[];
  /** Working days in each week: Mon–Fri minus company holidays. */
  weekDays: Iso[][];
}

export function buildCalendar(startMonday: Iso, weeks: number, holidays: ReadonlySet<Iso>): Calendar {
  const weekStarts: Iso[] = [];
  const weekDays: Iso[][] = [];
  for (let w = 0; w < weeks; w++) {
    const monday = addDays(startMonday, w * 7);
    weekStarts.push(monday);
    const days: Iso[] = [];
    for (let d = 0; d < 5; d++) {
      const day = addDays(monday, d);
      if (!holidays.has(day)) days.push(day);
    }
    weekDays.push(days);
  }
  return { weekStarts, weekDays };
}

export function rateOn(rates: readonly RateStep[], day: Iso): number {
  let rate = 0;
  for (const step of rates) {
    if (step.from > day) break;
    rate = step.rate;
  }
  return rate;
}

export interface CostResult {
  rowHours: Float64Array;
  rowCost: Float64Array;
  weekHours: Float64Array;
  weekCost: Float64Array;
  /** Hours by calendar month: a week straddling a month splits by working day. */
  monthHours: Map<Month, number>;
  monthCost: Map<Month, number>;
  totalHours: number;
  totalCost: number;
}

/**
 * Spreads each week's hours evenly over its working days and costs every day at the rate in
 * effect that day, so a raise effective on a Wednesday prorates exactly (§3.2).
 */
export function costPlan(cal: Calendar, rows: readonly CostRow[], hours: readonly Float64Array[]): CostResult {
  const weeks = cal.weekStarts.length;
  const rowHours = new Float64Array(rows.length);
  const rowCost = new Float64Array(rows.length);
  const weekHours = new Float64Array(weeks);
  const weekCost = new Float64Array(weeks);
  const monthHours = new Map<Month, number>();
  const monthCost = new Map<Month, number>();

  for (let r = 0; r < rows.length; r++) {
    const rates = rows[r].rates;
    const h = hours[r];
    for (let w = 0; w < weeks; w++) {
      const weekly = h[w];
      if (!weekly) continue;
      // A week that is all holidays has nowhere to put hours: spread them over Mon–Fri rather than
      // lose them. The app flags it in the data-quality inbox.
      const days = cal.weekDays[w].length ? cal.weekDays[w] : [0, 1, 2, 3, 4].map((d) => addDays(cal.weekStarts[w], d));
      const perDay = weekly / days.length;
      let cost = 0;
      for (const day of days) {
        const c = perDay * rateOn(rates, day);
        cost += c;
        const month = monthOf(day);
        monthHours.set(month, (monthHours.get(month) ?? 0) + perDay);
        monthCost.set(month, (monthCost.get(month) ?? 0) + c);
      }
      rowHours[r] += weekly;
      rowCost[r] += cost;
      weekHours[w] += weekly;
      weekCost[w] += cost;
    }
  }

  let totalHours = 0;
  let totalCost = 0;
  for (let r = 0; r < rows.length; r++) {
    totalHours += rowHours[r];
    totalCost += rowCost[r];
  }
  return { rowHours, rowCost, weekHours, weekCost, monthHours, monthCost, totalHours, totalCost };
}

export type LoadStatus = "over" | "on" | "under";

export interface LoadThresholds {
  over: number;
  under: number;
}

/** PRD §3.9 defaults: red above 110%, blue below 90%, teal between. Configurable (CM7). */
export const DEFAULT_THRESHOLDS: LoadThresholds = { over: 1.1, under: 0.9 };

export function statusOf(pct: number, t: LoadThresholds = DEFAULT_THRESHOLDS): LoadStatus {
  if (pct > t.over) return "over";
  if (pct < t.under) return "under";
  return "on";
}

/** Capacity for one week: capacity per day × working days that week. */
export const weekCapacity = (capacityPerWeek: number, workingDays: number): number => (capacityPerWeek / 5) * workingDays;

/**
 * Each named person's load per week across all projects: this plan's rows for them plus their hours
 * elsewhere, divided by that week's capacity net of holidays.
 */
export function loadByPerson(
  cal: Calendar,
  rows: readonly CostRow[],
  hours: readonly Float64Array[],
  otherProjects: Readonly<Record<string, Float64Array>>,
  capacityPerWeek: Readonly<Record<string, number>>,
  defaultCapacity = 45,
): Map<string, Float64Array> {
  const weeks = cal.weekStarts.length;
  const assigned = new Map<string, Float64Array>();
  for (const [person, other] of Object.entries(otherProjects)) assigned.set(person, Float64Array.from(other));
  rows.forEach((row, r) => {
    if (!row.person) return;
    let a = assigned.get(row.person);
    if (!a) assigned.set(row.person, (a = new Float64Array(weeks)));
    for (let w = 0; w < weeks; w++) a[w] += hours[r][w];
  });

  const load = new Map<string, Float64Array>();
  for (const [person, a] of assigned) {
    const capacity = capacityPerWeek[person] ?? defaultCapacity;
    const pct = new Float64Array(weeks);
    for (let w = 0; w < weeks; w++) {
      const cap = weekCapacity(capacity, cal.weekDays[w].length);
      pct[w] = cap > 0 ? a[w] / cap : a[w] > 0 ? Infinity : 0;
    }
    load.set(person, pct);
  }
  return load;
}
